import React from 'react';
import type { ChipType } from '@nuvolo/nuux-core/components/NuvoChip';
import { NuvoChip } from '@nuvolo/nuux-core/components/NuvoChip';
import { color } from '@nuvolo/nuux-theme/utils';
import styled from 'styled-components';

// Define a type for custom colors
export type CustomChipColors = {
  backgroundColor: string;
  textColor: string;
};

// Update the styled component to accept custom colors
export const TMMChip = styled(NuvoChip)<{
  type: ChipType;
  customColors?: CustomChipColors;
}>`
  && {
    border-radius: 8px;

    /* Apply custom colors if provided */
    ${(props) =>
      props.customColors &&
      `
      background-color: ${props.customColors.backgroundColor};
      
      div {
        color: ${props.customColors.textColor};
      }
    `}

    /* Default type-based styling if no custom colors provided */
    ${(props) =>
      !props.customColors &&
      props.type === 'info' &&
      `
      background-color: ${color('info', 'light3')};
      
      div {
        color: ${color('info', 'dark')};
      }
    `}

    /* Success type - green */
    ${(props) =>
      !props.customColors &&
      props.type === 'success' &&
      `
      background-color: ${color('success', 'light3')};
      
      div {
        color: ${color('success', 'dark')};
      }
    `}
    
    /* Warning type - amber/orange */
    ${(props) =>
      !props.customColors &&
      props.type === 'warn' &&
      `
      background-color: ${color('warn', 'light3')};
      
      div {
        color: ${color('warn', 'dark')};
      }
    `}
    
    /* Error type - red */
    ${(props) =>
      !props.customColors &&
      props.type === 'error' &&
      `
      background-color: ${color('error', 'light3')};
      
      div {
        color: ${color('error', 'dark')};
      }
    `}
    
    /* Accent type - purple */
    ${(props) =>
      !props.customColors &&
      props.type === 'accent' &&
      `
      background-color: ${color('accent', 'light3')};
      
      div {
        color: ${color('accent', 'dark')};
      }
    `}
    
    /* Normal type - gray */
    ${(props) =>
      !props.customColors &&
      props.type === 'normal' &&
      `
      background-color: ${color('ui', 'light3')};
      
      div {
        color: ${color('ui', 'dark')};
      }
    `}
  }
`;

// Updated withChip function that accepts custom colors
const withChip = (text: string, type: ChipType = 'info', customColors?: CustomChipColors) => {
  return <TMMChip label={text} type={type} customColors={customColors} />;
};

export default withChip;
